export type Disconnectable = {
  disconnect: () => void;
};

export type RegistryFactory<T extends Disconnectable> = () => T;

export function createRefCountedRegistry<T extends Disconnectable>() {
  const registry: Record<string, T> = {};
  const refCounts: Record<string, number> = {};

  const getOrCreate = (key: string, factory: RegistryFactory<T>): T => {
    if (!key) {
      throw new Error("key is required");
    }
    if (!registry[key]) {
      registry[key] = factory();
    }
    return registry[key];
  };

  const acquire = (key: string, factory: RegistryFactory<T>): T => {
    const manager = getOrCreate(key, factory);
    refCounts[key] = (refCounts[key] || 0) + 1;
    return manager;
  };

  const get = (key: string): T | null => {
    return registry[key] || null;
  };

  const destroy = (key: string): void => {
    const manager = registry[key];
    if (manager) {
      try {
        manager.disconnect();
      } catch {}
      delete registry[key];
    }
    delete refCounts[key];
  };

  const release = (key: string): void => {
    if (!key) return;
    if (!registry[key]) {
      delete refCounts[key];
      return;
    }
    const next = (refCounts[key] || 0) - 1;
    if (next <= 0) {
      destroy(key);
      return;
    }
    refCounts[key] = next;
  };

  const clearAll = (): void => {
    for (const key of Object.keys(registry)) {
      try {
        registry[key].disconnect();
      } catch {}
      delete registry[key];
      delete refCounts[key];
    }
  };

  return {
    getOrCreate,
    acquire,
    get,
    destroy,
    release,
    clearAll,
  };
}
